'use client';

import React, { FC, ReactNode, useState } from 'react';
import classNames from 'classnames';
import ChevronIcon from '@/components/ui/Icons/ChevronIcon';

export interface GenericAccordionProps {
  header: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
  headerClassName?: string;
  transitionDuration?: number;
  maxHeight?: string;
  ariaLabel?: string;
  testId?: string;
  showChevron?: boolean;
  chevronClassName?: string;
}

export const GenericAccordion: FC<GenericAccordionProps> = ({
  header,
  children,
  defaultOpen = false,
  onToggle,
  className = '',
  headerClassName = '',
  transitionDuration = 300,
  maxHeight = '2000px',
  ariaLabel,
  testId,
  showChevron = false,
  chevronClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  const transitionStyle = {
    transitionDuration: `${transitionDuration}ms`,
  };

  const contentStyle = {
    ...transitionStyle,
    maxHeight: isOpen ? maxHeight : '0',
    opacity: isOpen ? 1 : 0,
  };

  return (
    <div className={className} data-testid={testId}>
      {/* Accordion Header/Button */}
      <button
        className={classNames(headerClassName, {
          'flex items-center': showChevron,
        })}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        type="button"
      >
        {header}

        {showChevron && (
          <ChevronIcon
            direction={isOpen ? 'up' : 'down'}
            classes={classNames(
              `transition-transform duration-${transitionDuration}`,
              chevronClassName,
            )}
          />
        )}
      </button>

      {/* Accordion Content */}
      <div
        className="overflow-hidden transition-all ease-in-out"
        style={contentStyle}
      >
        {children}
      </div>
    </div>
  );
};

export default GenericAccordion;
